//frontend/src/components/providers/auth-provider.tsx

import React, { createContext, useContext, useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { auth, api } from '@/lib/api'
import { config } from '@/lib/config'
import type { User, LoginRequest, RegisterRequest } from '@/types'

interface AuthContextType {
    user: User | null
    isAuthenticated: boolean
    isLoading: boolean
    login: (credentials: LoginRequest) => Promise<void>
    register: (userData: RegisterRequest) => Promise<void>
    loginWithGoogle: (credential: string) => Promise<void>  // ✅ NEW
    logout: () => Promise<void>
    refreshUser: () => Promise<User | null>
    checkAuth: () => Promise<void>
    loginWithGitHub: (code: string) => Promise<void>

}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

interface AuthProviderProps {
    children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [isInitialized, setIsInitialized] = useState(false)
    const queryClient = useQueryClient()

    const isPublicPage = window.location.pathname.startsWith('/auth/') ||
        window.location.pathname === '/'

    // Query to get current user (only if token exists)
    const {
        data: user,
        isLoading: isUserLoading,
        error: userError,
        refetch: refetchUser,
    } = useQuery({
        queryKey: ['auth', 'user'],
        queryFn: auth.getCurrentUser,
        enabled: isInitialized && !isPublicPage,
        retry: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
    })

    // Login mutation
    const loginMutation = useMutation({
        mutationFn: auth.login,
        onSuccess: async (data) => {
            // CRITICAL: Set token FIRST
            api.setAccessToken(data.access_token)

            // Small delay to ensure token is set
            await new Promise(resolve => setTimeout(resolve, 50))

            // Now fetch user
            await refetchUser()

            queryClient.invalidateQueries({ queryKey: ['briefings'] })
            queryClient.invalidateQueries({ queryKey: ['settings'] })
        },
    })

    // Register mutation
    const registerMutation = useMutation({
        mutationFn: auth.register,
        onSuccess: async (data) => {
            api.setAccessToken(data.access_token)
            await refetchUser()
        },
    })

    // ✅ NEW: Google OAuth mutation
    const googleLoginMutation = useMutation({
        mutationFn: auth.loginWithGoogle,
        onSuccess: async (data) => {
            api.setAccessToken(data.access_token)
            await new Promise(resolve => setTimeout(resolve, 50))
            await refetchUser()
            queryClient.invalidateQueries({ queryKey: ['briefings'] })
            queryClient.invalidateQueries({ queryKey: ['settings'] })
        },
    })

    const githubLoginMutation = useMutation({
        mutationFn: auth.loginWithGitHub,
        onSuccess: async (data) => {
            api.setAccessToken(data.access_token)
            await new Promise(resolve => setTimeout(resolve, 50))
            await refetchUser()
            queryClient.invalidateQueries({ queryKey: ['briefings'] })
            queryClient.invalidateQueries({ queryKey: ['settings'] })
        },
    })




    // Check auth function for email verification
    const checkAuth = async () => {
        try {
            await refetchUser()
        } catch (error) {
            console.error('Failed to refresh auth:', error)
        }
    }

    // Logout mutation
    const logoutMutation = useMutation({
        mutationFn: async () => {
            console.log('🔴 logoutMutation.mutationFn executing')
            await auth.logout()
        },
        onSuccess: () => {
            console.log('🔴 logoutMutation.onSuccess')
            queryClient.clear()
            queryClient.setQueryData(['auth', 'user'], null)
            window.location.href = '/auth/login'
        },
    })

    // Initialize auth state

    useEffect(() => {
        api.tryRestoreSession().then(() => setIsInitialized(true))
    }, [])

    // Handle authentication errors
    useEffect(() => {
        if (userError) {
            console.error('User fetch error:', userError)
            api.setAccessToken(null)
        }
    }, [userError])

    // Demo mode handling
    useEffect(() => {
        if (config.isDemo && !user && isInitialized) {
            // In demo mode, automatically set demo user
            const loadDemoData = async () => {
                const demoData = await api.getDemoData()
                queryClient.setQueryData(['auth', 'user'], demoData.user)
            }
            loadDemoData()
        }
    }, [config.isDemo, user, isInitialized, queryClient])

    const contextValue: AuthContextType = {
        user: user || null,
        isAuthenticated: !!user,
        isLoading: !isInitialized || isUserLoading,

        login: async (credentials: LoginRequest) => {
            try {
                await loginMutation.mutateAsync(credentials)
            } catch (error) {
                throw error
            }
        },

        register: async (userData: RegisterRequest) => {
            try {
                await registerMutation.mutateAsync(userData)
            } catch (error) {
                throw error
            }
        },

        // ✅ NEW: Google OAuth login
        loginWithGoogle: async (credential: string) => {
            try {
                await googleLoginMutation.mutateAsync(credential)
            } catch (error) {
                throw error
            }
        },




        // ✅ Wrapper function
        loginWithGitHub: async (code: string) => {
            try {
                await githubLoginMutation.mutateAsync(code)
            } catch (error) {
                throw error
            }
        },



        logout: async () => {
            console.log('🔴 AUTH PROVIDER logout() called')
            try {
                await logoutMutation.mutateAsync()
                console.log('🔴 logoutMutation completed')
            } catch (error) {
                console.error('🔴 Auth provider logout error:', error)
            }
        },

        refreshUser: async () => {
            try {
                // Force invalidate the current query
                queryClient.invalidateQueries({ queryKey: ['auth', 'user'] })

                // Fetch fresh data directly from the API
                const freshUser = await auth.getCurrentUser()

                // Update the cache manually
                queryClient.setQueryData(['auth', 'user'], freshUser)

                return freshUser
            } catch (error) {
                console.error('Refresh user error:', error)
                return null
            }
        },

        checkAuth: async () => {
            try {
                await refetchUser()
            } catch (error) {
                console.error('Failed to refresh auth:', error)
            }
        },
    }

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    )
}